/**
 * 종목 캔들차트 공통 컴포넌트 — 시장인사이트 페이지 / 종목 상세 드로어 공용
 * 기간 Segmented + 캔들 + 거래량 + 기간 내 고/저점의 현재가 대비 % 마크
 * chartQ를 내장 (queryKey: ["market","chart",symbol,days,overseas])
 */
import { Card, Empty, Segmented, Spin } from "antd";
import ReactECharts from "src/lib/EChart";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { marketApi } from "src/api/market";
import { stocksApi } from "src/api/stocks";
import { registerChartTheme, useChartTheme } from "src/components/charts/chartTheme";
import { useThemeStore } from "src/stores/ThemeStore";
import { USERS } from "src/lib/users";

registerChartTheme();

/** YTD = 올해 거래일 수(주말 제외) — 모듈 로드 시 1회 계산 (캔들 단위가 거래일이므로 1Y=250과 동일 기준) */
const ytdTradingDays = (): number => {
  let n = 0;
  for (let d = dayjs().startOf("year"); d.isBefore(dayjs(), "day"); d = d.add(1, "day")) {
    if (d.day() !== 0 && d.day() !== 6) n++;
  }
  return Math.max(1, n);
};

export const PERIOD_PRESETS: Record<string, number> = { "1M": 22, "3M": 66, "6M": 130, YTD: ytdTradingDays(), "1Y": 250 };

interface StockCandleChartProps {
  symbol: string;
  overseas?: boolean;
  /** 차트 높이 px (기본 280) */
  height?: number;
  /** 이 값이 바뀌면 기간을 6M으로 리셋 (드로어 open 등) */
  resetKey?: string | boolean;
  /** 쿼리 활성화 (드로어 open 시에만) */
  enabled?: boolean;
  marginBottom?: number;
  /** 수익금 뷰 활성화 — 보유 종목 드로어에서만 전달 (거래내역 기반 계산) */
  gainEnabled?: boolean;
  /** 수익금 계산용 — positions의 stock_id(.KS 접미사 포함). 거래내역 필터에 사용 */
  stockId?: string;
  /** 거래내역 조회 범위 (수익금 계산용) — StockHistoryCard와 쿼리 키 공유 */
  userIds?: string[];
}

const StockCandleChart = ({
  symbol,
  overseas = false,
  height = 280,
  resetKey,
  enabled = true,
  marginBottom = 12,
  gainEnabled = false,
  stockId,
  userIds,
}: StockCandleChartProps) => {
  const isDark = useThemeStore((s) => s.isDarkMode);
  const chartTheme = useChartTheme();
  const [period, setPeriod] = useState<string>("6M");
  const [view, setView] = useState<"price" | "gain">("price");

  useEffect(() => {
    setPeriod("6M");
    setView("price");
  }, [resetKey, symbol]);

  const chartQ = useQuery({
    queryKey: ["market", "chart", symbol, PERIOD_PRESETS[period], overseas],
    queryFn: () => marketApi.getChart(symbol, PERIOD_PRESETS[period], overseas),
    enabled: enabled && !!symbol,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  // 수익금 계산용 거래내역 — 드로어의 StockHistoryCard와 동일 쿼리 키라 캐시 공유 (추가 API 0회)
  const gainScope = userIds ?? USERS.map((u) => u.id);
  const txQ = useQuery({
    queryKey: ["stocks", "transactions", gainScope],
    queryFn: () => stocksApi.getTransactions(gainScope),
    enabled: enabled && gainEnabled && !!stockId,
    staleTime: 5 * 60_000,
  });

  const textColor = isDark ? "#ccc" : "#333";
  const gridColor = isDark ? "#333" : "#eee";
  const upColor = "#ff4d4f";
  const downColor = "#1890ff";

  const candleOption = useMemo(() => {
    const candles = chartQ.data?.candles || [];
    if (candles.length === 0) return null;

    // 기간 내 최고/최저점 → 현재가(마지막 종가) 대비 등락률
    let hiIdx = 0;
    let loIdx = 0;
    candles.forEach((c, i) => {
      if (c.high > candles[hiIdx].high) hiIdx = i;
      if (c.low < candles[loIdx].low) loIdx = i;
    });
    const lastClose = candles[candles.length - 1].close;
    const hiPct = ((candles[hiIdx].high - lastClose) / lastClose) * 100;
    const loPct = ((candles[loIdx].low - lastClose) / lastClose) * 100;

    // 툴팁용 전일 대비 사전 계산 (index 0은 전일 데이터 없음)
    const dayChanges = candles.map((c, i) => {
      if (i === 0) return null;
      const prev = candles[i - 1].close;
      const diff = c.close - prev;
      const pct = prev > 0 ? (diff / prev) * 100 : 0;
      return { diff, pct };
    });

    const fmtPrice = (v: number) =>
      overseas ? `$${v.toFixed(2)}` : `${Math.round(v).toLocaleString("ko-KR")}원`;
    const fmtChange = (d: { diff: number; pct: number } | null) => {
      if (!d) return "";
      const sign = d.diff >= 0 ? "+" : "-";
      const color = d.diff > 0 ? upColor : d.diff < 0 ? downColor : "#999";
      const amount = overseas
        ? `$${Math.abs(d.diff).toFixed(2)}`
        : `${Math.round(Math.abs(d.diff)).toLocaleString("ko-KR")}원`;
      return `<br/><span style="color:${color}">전일 대비 ${sign}${amount} (${sign}${Math.abs(d.pct).toFixed(2)}%)</span>`;
    };

    return {
      tooltip: {
        trigger: "axis" as const,
        axisPointer: { type: "cross" as const },
        formatter: (ps: any[]) => {
          const i = ps[0]?.dataIndex;
          const c = candles[i];
          if (!c) return "";
          const chg = dayChanges[i];
          const volume = (c.volume || 0).toLocaleString("ko-KR");
          let s = `<b>${c.time}</b><br/>`;
          s += `시가 ${fmtPrice(c.open)} · 고가 ${fmtPrice(c.high)}<br/>`;
          s += `저가 ${fmtPrice(c.low)} · 종가 <b>${fmtPrice(c.close)}</b><br/>`;
          s += `거래량 ${volume}`;
          s += fmtChange(chg);
          return s;
        },
      },
      grid: [
        { top: 22, left: 62, right: 12, height: "56%" },
        { top: "74%", left: 62, right: 12, bottom: 22 },
      ],
      xAxis: [
        { type: "category" as const, data: candles.map((c) => c.time.slice(5)), axisLabel: { color: textColor, fontSize: 10 }, axisLine: { lineStyle: { color: gridColor } } },
        { type: "category" as const, gridIndex: 1, data: candles.map((c) => c.time.slice(5)), axisLabel: { show: false }, axisLine: { lineStyle: { color: gridColor } } },
      ],
      yAxis: [
        { type: "value" as const, scale: true, axisLabel: { color: textColor, fontSize: 10 }, splitLine: { lineStyle: { color: gridColor } } },
        { type: "value" as const, gridIndex: 1, axisLabel: { show: false }, splitLine: { show: false } },
      ],
      series: [
        {
          name: "가격",
          type: "candlestick" as const,
          data: candles.map((c) => [c.open, c.close, c.low, c.high]),
          itemStyle: { color: upColor, color0: downColor, borderColor: upColor, borderColor0: downColor },
          markPoint: {
            symbol: "circle",
            symbolSize: 5,
            animation: false,
            data: [
              {
                coord: [hiIdx, candles[hiIdx].high],
                value: `고 ${hiPct >= 0 ? "+" : ""}${hiPct.toFixed(1)}%`,
                itemStyle: { color: upColor },
                label: { show: true, position: "top", fontSize: 10, fontWeight: 600, color: upColor, distance: 5 },
              },
              {
                coord: [loIdx, candles[loIdx].low],
                value: `저 ${loPct >= 0 ? "+" : ""}${loPct.toFixed(1)}%`,
                itemStyle: { color: downColor },
                label: { show: true, position: "bottom", fontSize: 10, fontWeight: 600, color: downColor, distance: 5 },
              },
            ],
          },
        },
        {
          name: "거래량",
          type: "bar" as const,
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: candles.map((c) => ({
            value: c.volume || 0,
            itemStyle: { color: c.close >= c.open ? "rgba(255,77,79,0.5)" : "rgba(24,144,255,0.5)" },
          })),
        },
      ],
    };
  }, [chartQ.data, isDark]);

  /** 수익금 차트 — 이동평균법 평단가 역산: 시점별 수익금 라인 + 투자금(평단가×수량) 바 */
  const gainOption = useMemo(() => {
    const candles = chartQ.data?.candles || [];
    if (candles.length === 0 || !stockId) return null;

    // 해당 종목 거래를 과거순으로 정렬해 시점별 수량·이동평균 평단가 누적 (프로젝트 표준: 이동평균법)
    const txns = (txQ.data || [])
      .filter((t) => t.stock_id === stockId)
      .sort((a, b) => a.transaction_date.localeCompare(b.transaction_date));
    let qty = 0;
    let avgPrice = 0;
    let txIdx = 0;
    const points: { time: string; close: number; qty: number; avgPrice: number }[] = [];
    for (const c of candles) {
      const date = c.time.slice(0, 10);
      while (txIdx < txns.length && txns[txIdx].transaction_date.slice(0, 10) <= date) {
        const t = txns[txIdx];
        if (t.transaction_type === "BUY") {
          const newQty = qty + t.quantity;
          if (newQty > 0) avgPrice = (qty * avgPrice + t.quantity * t.price) / newQty;
          qty = newQty;
        } else {
          // 매도 — 수량만 감소, 평단가 불변 (이동평균법)
          qty = Math.max(qty - t.quantity, 0);
        }
        txIdx++;
      }
      if (qty > 0) points.push({ time: date, close: c.close, qty, avgPrice });
    }
    if (points.length === 0) return null;

    const fmt = (v: number) =>
      overseas ? `$${v.toLocaleString("ko-KR", { maximumFractionDigits: 0 })}` : `${Math.round(v).toLocaleString("ko-KR")}`;

    /** markPoint 라벨용 축약 포맷 — 100만 미만=기존 풀 포맷, 이상=KRW `x,xxx만원` / USD `$x,xxx` */
    const fmtCompact = (v: number) => {
      const abs = Math.abs(v);
      if (abs < 1_000_000) return fmt(abs);
      return overseas ? `$${Math.round(abs).toLocaleString("ko-KR")}` : `${Math.round(abs / 10_000).toLocaleString("ko-KR")}만원`;
    };

    let hiIdx = 0;
    let loIdx = 0;
    points.forEach((p, i) => {
      if (p.close - p.avgPrice > points[hiIdx].close - points[hiIdx].avgPrice) hiIdx = i;
      if (p.close - p.avgPrice < points[loIdx].close - points[loIdx].avgPrice) loIdx = i;
    });

    return {
      tooltip: {
        trigger: "axis" as const,
        formatter: (ps: any[]) => {
          const i = ps[0]?.dataIndex;
          const p = points[i];
          if (!p) return "";
          const gain = (p.close - p.avgPrice) * p.qty;
          const invest = p.avgPrice * p.qty;
          const color = gain > 0 ? upColor : gain < 0 ? downColor : "#999";
          const sign = gain >= 0 ? "+" : "-";
          return (
            `<b>${p.time}</b><br/>` +
            `보유 ${p.qty.toLocaleString("ko-KR")}주 · 평단 ${fmt(p.avgPrice)}<br/>` +
            `투자금 ${fmt(invest)} · 평가 ${fmt(p.close * p.qty)}<br/>` +
            `<span style="color:${color}">수익금 ${sign}${fmt(Math.abs(gain))} (${sign}${((gain / invest) * 100).toFixed(2)}%)</span>`
          );
        },
      },
      grid: [
        { top: 22, left: 62, right: 12, height: "56%" },
        { top: "74%", left: 62, right: 12, bottom: 22 },
      ],
      xAxis: [
        { type: "category" as const, data: points.map((p) => p.time.slice(5)), axisLabel: { color: textColor, fontSize: 10 }, axisLine: { lineStyle: { color: gridColor } } },
        { type: "category" as const, gridIndex: 1, data: points.map((p) => p.time.slice(5)), axisLabel: { show: false }, axisLine: { lineStyle: { color: gridColor } } },
      ],
      yAxis: [
        { type: "value" as const, scale: true, axisLabel: { color: textColor, fontSize: 10, formatter: (v: number) => fmt(v) }, splitLine: { lineStyle: { color: gridColor } } },
        { type: "value" as const, gridIndex: 1, axisLabel: { show: false }, splitLine: { show: false } },
      ],
      series: [
        {
          name: "수익금",
          type: "line" as const,
          data: points.map((p) => Math.round((p.close - p.avgPrice) * p.qty)),
          symbol: "none",
          lineStyle: { width: 2, color: "#722ed1" },
          itemStyle: { color: "#722ed1" },
          areaStyle: { color: "rgba(114,46,209,0.08)" },
          markPoint: {
            symbol: "circle",
            symbolSize: 5,
            animation: false,
            data: [
              {
                coord: [hiIdx, Math.round((points[hiIdx].close - points[hiIdx].avgPrice) * points[hiIdx].qty)],
                value: `고 ${fmtCompact((points[hiIdx].close - points[hiIdx].avgPrice) * points[hiIdx].qty)}`,
                itemStyle: { color: upColor },
                label: { show: true, position: "top", fontSize: 10, fontWeight: 600, color: upColor, distance: 5, formatter: (p: any) => p.value },
              },
              {
                coord: [loIdx, Math.round((points[loIdx].close - points[loIdx].avgPrice) * points[loIdx].qty)],
                value: `저 ${fmtCompact((points[loIdx].close - points[loIdx].avgPrice) * points[loIdx].qty)}`,
                itemStyle: { color: downColor },
                label: { show: true, position: "bottom", fontSize: 10, fontWeight: 600, color: downColor, distance: 5, formatter: (p: any) => p.value },
              },
            ],
          },
        },
        {
          name: "투자금",
          type: "bar" as const,
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: points.map((p) => ({
            value: Math.round(p.avgPrice * p.qty),
            itemStyle: { color: "rgba(114,46,209,0.35)" },
          })),
        },
      ],
    };
  }, [chartQ.data, txQ.data, stockId, overseas, isDark]);

  return (
    <Card size="small" style={{ marginBottom }} styles={{ body: { padding: "8px 12px" } }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginBottom: 4 }}>
        {gainEnabled ? (
          <Segmented
            size="small"
            value={view}
            onChange={(v) => setView(v as "price" | "gain")}
            options={[
              { label: "주가", value: "price" },
              { label: "수익금", value: "gain" },
            ]}
          />
        ) : <span />}
        <Segmented size="small" value={period} onChange={(v) => setPeriod(v as string)} options={Object.keys(PERIOD_PRESETS).map((k) => ({ label: k, value: k }))} />
      </div>
      <Spin spinning={(view === "price" ? chartQ.isLoading : gainEnabled && txQ.isLoading)}>
        {view === "price" ? (
          chartQ.isError ? (
            <Empty description="차트 데이터를 불러올 수 없습니다" style={{ margin: "50px 0" }} />
          ) : candleOption ? (
            <ReactECharts option={candleOption} style={{ height }} theme={chartTheme} lazyUpdate />
          ) : (
            <Empty description="차트 데이터 없음" />
          )
        ) : gainEnabled ? (
          !txQ.isError && gainOption ? (
            <ReactECharts option={gainOption} style={{ height }} theme={chartTheme} lazyUpdate />
          ) : (
            <Empty description={txQ.isError ? "거래내역을 불러올 수 없습니다" : "보유 중이던 기간이 없습니다"} style={{ margin: "50px 0" }} />
          )
        ) : null}
      </Spin>
    </Card>
  );
};

export default StockCandleChart;
